import {
  LogoutOutlined,
  SettingOutlined,
  UserOutlined,
} from '@ant-design/icons';
import { history, useModel } from '@umijs/max';
import { Avatar, Button, Space } from 'antd';
import { createStyles } from 'antd-style';
import type { MenuInfo } from 'rc-menu/lib/interface';
import React, { useCallback } from 'react';
import { flushSync } from 'react-dom';
import HeaderDropdown from '../HeaderDropdown';

const useAvatarStyles = createStyles(({ css, token }) => ({
  avatarContainer: css`
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 4px 8px;
    border-radius: 24px;
    transition: all 0.3s ease;
    cursor: pointer;
    &:hover {
      background: rgba(0, 0, 0, 0.04);
    }
  `,
  avatar: css`
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
    display: flex;
    align-items: center;
    justify-content: center;
    color: white;
    font-weight: 500;
    font-size: 16px;
    box-shadow: 0 2px 8px rgba(102, 126, 234, 0.3);
    transition: all 0.3s ease;
    &:hover {
      transform: scale(1.05);
      box-shadow: 0 4px 12px rgba(102, 126, 234, 0.4);
    }
  `,
  userName: css`
    font-weight: 500;
    color: ${token.colorText};
    max-width: 120px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  `,
  loginButton: css`
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
    border: none;
    box-shadow: 0 2px 8px rgba(102, 126, 234, 0.3);
    transition: all 0.3s ease;
    &:hover {
      transform: translateY(-1px);
      box-shadow: 0 4px 12px rgba(102, 126, 234, 0.4);
    }
    &:active {
      transform: translateY(0);
    }
  `,
}));

export type GlobalHeaderRightProps = {
  menu?: boolean;
};

export const AvatarDropdown: React.FC<GlobalHeaderRightProps> = ({ menu }) => {
  const { styles } = useAvatarStyles();
  /**
   * 退出登录：清除 Token 后跳回登录页
   */
  const loginOut = () => {
    sessionStorage.removeItem('token');
    const { search, pathname } = window.location;
    const urlParams = new URL(window.location.href).searchParams;
    const redirect = urlParams.get('redirect');
    if (window.location.pathname !== '/user/login' && !redirect) {
      const params = new URLSearchParams();
      if (pathname + search !== '/') {
        params.set('redirect', pathname + search);
      }
      history.replace(`/user/login?${params.toString()}`);
    }
  };

  const { initialState, setInitialState } = useModel('@@initialState');

  const onMenuClick = useCallback(
    (event: MenuInfo) => {
      const { key } = event;
      if (key === 'logout') {
        flushSync(() => {
          setInitialState((s: Record<string, unknown>) => ({
            ...s,
            currentUser: undefined,
          }));
        });
        loginOut();
        return;
      }
      history.push(`/account/${key}`);
    },
    [setInitialState],
  );

  const { currentUser } = initialState || {};

  if (!currentUser) {
    return (
      <a href="/user/login">
        <Button type="primary" shape="round" className={styles.loginButton}>
          登录
        </Button>
      </a>
    );
  }

  const menuItems = [
    ...(menu
      ? [
          {
            key: 'center',
            icon: <UserOutlined />,
            label: '个人中心',
          },
          {
            key: 'settings',
            icon: <SettingOutlined />,
            label: '个人设置',
          },
          {
            type: 'divider' as const,
          },
        ]
      : []),
    {
      key: 'logout',
      icon: <LogoutOutlined />,
      label: '退出登录',
    },
  ];

  return (
    <HeaderDropdown
      menu={{
        selectedKeys: [],
        onClick: onMenuClick,
        items: menuItems,
      }}
    >
      <Space>
        <Avatar size="small" src="/avatar.png" icon={<UserOutlined />} />
        <span className="anticon">{currentUser?.name ?? '未登录'}</span>
      </Space>
    </HeaderDropdown>
  );
};

export const AvatarName = () => {};
